/**
 * Agent Modes endpoints — list + full CRUD + versioning.
 *
 * Ports v1 ``ando-frontend/app/api/agentModes.ts``. Wire types live in
 * ``../../domain/mode.ts`` (canonical zod schemas re-used by the
 * conversation store and the studio drawer).
 *
 * BE source of truth: ``ando-agent/src/api/v1/endpoints/agent_modes.py``.
 * Every endpoint here is gated by the ``"modes"`` capability and the
 * RBAC matrix:
 *   - scope=user   → any authenticated caller (owns only themselves)
 *   - scope=org    → org owner/admin of the target org, OR platform admin
 *   - scope=global → platform admin only
 *
 * Hybrid 3-tier scope (see ``AgentModeScope``):
 *   - ``user``   — visible only to ``owner_user_id``
 *   - ``org``    — visible to every active member of ``org_id``
 *   - ``global`` — visible platform-wide
 */

import type {
  AgentModeDefaultConfig,
  AgentModeScope,
  AgentModeVersion,
  Mode,
} from "../../domain/mode.js";
import { apiClient } from "../ApiClient.js";

const BASE = "/agent-modes";

/** BE response envelope from ``list_modes``. */
export interface AgentModeListResponse {
  items: Mode[];
  total: number;
}

/** BE response envelope from ``list_mode_versions``. */
export interface AgentModeVersionsResponse {
  items: AgentModeVersion[];
  total: number;
}

export interface CreateAgentModeRequest {
  name: string;
  description?: string;
  icon?: string;
  scope: AgentModeScope;
  org_id?: string;
  system_prompt_overlay: string;
  default_config: AgentModeDefaultConfig;
}

export interface UpdateAgentModeMetadataRequest {
  name?: string;
  description?: string;
  icon?: string;
}

export interface CreateAgentModeVersionRequest {
  system_prompt_overlay: string;
  default_config: AgentModeDefaultConfig;
  change_summary?: string;
}

/** GET ``/agent-modes`` — list every mode visible to the caller.
 *
 * Defensive shape handling (May 25, 2026 — Andrea "studio vede vuoto"):
 * the BE envelope is ``{items, total}`` per ``AgentModeListResponse`` in
 * ``agent_modes.py`` but production briefly returned a bare array under
 * an older ``response_model`` shape, leaving the client with
 * ``res.items === undefined`` → ``[].map`` crash + an empty composer
 * dropdown / Studio drawer even when the user has modes. Accept both
 * shapes so the consumer never sees ``undefined``.
 */
export async function listAgentModes(): Promise<Mode[]> {
  const res = await apiClient.get<AgentModeListResponse | Mode[]>(BASE);
  if (Array.isArray(res)) return res;
  return res?.items ?? [];
}

/** GET ``/agent-modes/{id}`` — full payload for one mode. */
export async function getAgentMode(modeId: string): Promise<Mode> {
  return apiClient.get<Mode>(`${BASE}/${modeId}`);
}

/** POST ``/agent-modes`` — create a new mode + its initial v1 version. */
export async function createAgentMode(
  data: CreateAgentModeRequest,
): Promise<Mode> {
  return apiClient.post<Mode>(BASE, data);
}

/** PATCH ``/agent-modes/{id}`` — update metadata only (name/description/icon). */
export async function updateAgentModeMetadata(
  modeId: string,
  data: UpdateAgentModeMetadataRequest,
): Promise<Mode> {
  return apiClient.patch<Mode>(`${BASE}/${modeId}`, data);
}

/** DELETE ``/agent-modes/{id}`` — soft-delete (sets ``is_active=false``). */
export async function deleteAgentMode(modeId: string): Promise<void> {
  await apiClient.delete<void>(`${BASE}/${modeId}`);
}

/** POST ``/agent-modes/{id}/versions`` — append a new version + flip current. */
export async function createAgentModeVersion(
  modeId: string,
  data: CreateAgentModeVersionRequest,
): Promise<AgentModeVersion> {
  return apiClient.post<AgentModeVersion>(`${BASE}/${modeId}/versions`, data);
}

/** GET ``/agent-modes/{id}/versions`` — full history (DESC by version_number). */
export async function listAgentModeVersions(
  modeId: string,
): Promise<AgentModeVersion[]> {
  const res = await apiClient.get<AgentModeVersionsResponse>(
    `${BASE}/${modeId}/versions`,
  );
  return res.items;
}

/** POST ``/agent-modes/{id}/rollback`` — flip ``current_version_id`` back. */
export async function rollbackAgentMode(
  modeId: string,
  targetVersionId: string,
): Promise<Mode> {
  return apiClient.post<Mode>(`${BASE}/${modeId}/rollback`, {
    target_version_id: targetVersionId,
  });
}

/**
 * Re-export under the v1 alias so call-sites can type imports as ``AgentMode``
 * (the name used by v1 + most BE docs). The canonical type lives in
 * ``packages/core/src/domain/mode.ts`` as ``Mode``.
 */
export type { Mode as AgentMode } from "../../domain/mode.js";
